import { api } from "./client";
import type { UsdBrl } from "../composables/fx";

export interface PlatformOverview {
  customers: {
    total: number;
    byLifecycle: Record<string, number>;
    byDbState: Record<string, number>;
  };
  month: {
    conversationsOpened: number;
    conversationsClosed: number;
    messagesInbound: number;
    messagesOutbound: number;
    aiCostMicros: number;
    /** Per currency: Meta bills each account in its own. */
    metaCost: Array<{ currency: string; micros: number }>;
  };
  /** Today's dollar, to show AI spend in reais too. */
  fx: UsdBrl | null;
  days: Array<{
    day: string;
    conversationsOpened: number;
    messagesOutbound: number;
    aiCostMicros: number;
  }>;
}
export interface CustomerRow {
  id: string;
  slug: string;
  name: string;
  lifecycle: string;
  dbState: string;
  dbMigratedTo: string | null;
  planId: string | null;
  planCode: string | null;
  dbInstance: string | null;
  defaultLocale: string;
  timezone: string;
  trialEndsAt: string | null;
  suspendedAt: string | null;
  createdAt: string;
  members: number;
  /** Invitations not yet accepted. */
  invited?: number;
  owners: Array<{ id: string; email: string; name: string }>;
  /** Where the customer is in the setup journey, as its workspace last published it. */
  onboarding?: {
    stagesDone: number;
    stages: number;
    current: string | null;
    liveAt: string | null;
    stuck: string[];
    updatedAt: string;
  } | null;
}
export interface CustomerDetail {
  customer: CustomerRow & {
    legalName: string | null;
    taxId: string | null;
    country: string;
    dbName: string;
    visibilityMode: string;
    settings: Record<string, unknown>;
    churnedAt: string | null;
    updatedAt: string;
  };
  memberships: Array<{
    userId: string;
    email: string;
    name: string;
    role: string;
    status: string;
    lastSeenAt: string | null;
    mfaEnabled?: boolean;
  }>;
  stats: {
    contacts: number | null;
    conversationsOpen: number | null;
    conversationsClosedMonth: number | null;
    channels: number | null;
    flowsPublished: number | null;
    assistantsActive: number | null;
  };
  usage: Record<string, unknown>;
  /** A Multi-Partner Solution is registered, so partner billing can reach Meta. */
  metaSolution?: boolean;
}
/** A customer's number as its own database knows it. */
export interface ChannelRow {
  id: string;
  kind: string;
  address: string | null;
  displayName: string | null;
  status: string;
  quality: string | null;
  tier: string | null;
  nameStatus: string | null;
  connectionMethod: string | null;
  canSend: string | null;
  healthCheckedAt: string | null;
  connectedAt: string;
  account: { id: string; name: string | null; currency: string | null } | null;
}
export interface PlanRow {
  id: string;
  code: string;
  name: Record<string, string> | string;
  description?: Record<string, string>;
  /** Reais per month; null = on request. */
  priceBrl?: number | null;
  /** Reais per customer served beyond the monthly quota. */
  extraCustomerBrl?: number | null;
  sort?: number;
  limits: Record<string, unknown>;
  features: Record<string, unknown>;
  isActive: boolean;
  customers: number;
}
export interface AdminRow {
  userId: string;
  email: string;
  name: string;
  role: "super" | "support";
  createdAt: string;
}
export interface PlatformEvent {
  id: string;
  occurredAt: string;
  actorUserId: string | null;
  actorEmail: string | null;
  action: string;
  entity: { kind: string | null; id: string | null };
  data: unknown;
  requestId: string | null;
}
export interface AiModelRow {
  id: string;
  provider_code?: string;
  providerCode?: string;
  code?: string;
  model_id?: string;
  display_name?: string;
  name?: string;
  input_price_micros?: number | null;
  output_price_micros?: number | null;
  is_enabled?: boolean;
  enabled?: boolean;
  [k: string]: unknown;
}

export const platformApi = {
  overview: () => api<PlatformOverview>("/v1/platform/overview"),
  /** Support for someone locked out; the reason goes to the platform audit. */
  userPasswordReset: (userId: string, reason: string) =>
    api<{ token: string; expiresAt: string }>(
      `/v1/platform/users/${userId}/password-reset`,
      { method: "POST", body: { reason } },
    ),
  userMfaReset: (userId: string, reason: string) =>
    api<{ ok: true }>(`/v1/platform/users/${userId}/mfa/reset`, {
      method: "POST",
      body: { reason },
    }),
  customers: (q?: { q?: string; lifecycle?: string }) =>
    api<{ customers: CustomerRow[] }>("/v1/platform/customers", {
      query: q ?? {},
    }),
  customer: (id: string) => api<CustomerDetail>(`/v1/platform/customers/${id}`),
  createCustomer: (body: {
    slug: string;
    name: string;
    ownerEmail: string;
    ownerName: string;
    lifecycle?: "trial" | "active";
    defaultLocale?: string;
    planId?: string | null;
    legalName?: string | null;
    taxId?: string | null;
    timezone?: string;
  }) =>
    api<{
      customer: { id: string; slug: string; name: string };
      owner: {
        id: string;
        email: string;
        hasPassword: boolean;
        status: "active" | "invited";
      };
      /** The owner's invitation, to hand over when e-mail did not go. */
      inviteLink: string | null;
      emailed: boolean;
    }>("/v1/platform/customers", { method: "POST", body }),
  patchCustomer: (id: string, body: Record<string, unknown>) =>
    api("/v1/platform/customers/" + id, { method: "PATCH", body }),
  provision: (id: string) =>
    api<{ ok: true; dbState: string }>(
      `/v1/platform/customers/${id}/provision`,
      { method: "POST", body: {} },
    ),
  addOwner: (id: string, body: { email: string; name: string }) =>
    api<{
      user: { id: string; email: string; hasPassword: boolean };
      inviteToken: string | null;
      inviteLink: string | null;
      emailed: boolean;
    }>(`/v1/platform/customers/${id}/owners`, { method: "POST", body }),
  channels: (id: string) =>
    api<{ channels: ChannelRow[] }>(`/v1/platform/customers/${id}/channels`),
  supportStatus: (id: string) =>
    api<{
      allowed: boolean;
      mine: {
        id: string;
        level: "view" | "edit";
        reason: string;
        expiresAt: string;
      } | null;
      open: number;
    }>(`/v1/platform/customers/${id}/support-access`),
  startSupport: (
    id: string,
    body: { reason: string; minutes: number; level: "view" | "edit" },
  ) =>
    api<{
      access: { id: string; level: string; expiresAt: string };
      slug: string;
    }>(`/v1/platform/customers/${id}/support-access`, { method: "POST", body }),
  endSupport: (id: string) =>
    api(`/v1/platform/customers/${id}/support-access`, { method: "DELETE" }),
  plans: () => api<{ plans: PlanRow[] }>("/v1/platform/plans"),
  createPlan: (body: {
    code: string;
    name: Record<string, string> | string;
    description?: Record<string, string>;
    priceBrl?: number | null;
    extraCustomerBrl?: number | null;
    sort?: number;
    limits?: Record<string, unknown>;
    features?: Record<string, unknown>;
  }) => api<{ plan: PlanRow }>("/v1/platform/plans", { method: "POST", body }),
  patchPlan: (id: string, body: Record<string, unknown>) =>
    api(`/v1/platform/plans/${id}`, { method: "PATCH", body }),
  admins: () => api<{ admins: AdminRow[] }>("/v1/platform/admins"),
  addAdmin: (body: {
    email: string;
    role: "super" | "support";
    name?: string;
  }) =>
    api<{ admin: AdminRow; passwordLink: string | null }>(
      "/v1/platform/admins",
      { method: "POST", body },
    ),
  removeAdmin: (userId: string) =>
    api(`/v1/platform/admins/${userId}`, { method: "DELETE" }),
  audit: (q: {
    limit?: number;
    cursor?: string;
    customerId?: string;
    action?: string;
  }) =>
    api<{ events: PlatformEvent[]; nextCursor: string | null }>(
      "/v1/platform/audit",
      { query: q },
    ),
  aiModels: () => api<{ models: AiModelRow[] }>("/v1/platform/ai/models"),
  refreshModels: () =>
    api("/v1/platform/ai/models/refresh", { method: "POST", body: {} }),
  syncPricing: () =>
    api<{
      updated: number;
      skippedManual: number;
      unpriced: string[];
      alerts: Array<
        | {
            kind: "price_moved";
            model: string;
            field: "input" | "output";
            from: number;
            to: number;
          }
        | { kind: "retiring"; model: string; date: string }
      >;
    }>("/v1/platform/ai/pricing/sync", { method: "POST", body: {} }),
  patchModel: (id: string, body: Record<string, unknown>) =>
    api(`/v1/platform/ai/models/${id}`, { method: "PATCH", body }),
  aiKeys: () =>
    api<{ keys: Array<Record<string, unknown> & { id: string }> }>(
      "/v1/platform/ai/keys",
    ),
  addAiKey: (body: Record<string, unknown>) =>
    api("/v1/platform/ai/keys", { method: "POST", body }),
  deleteAiKey: (id: string) =>
    api(`/v1/platform/ai/keys/${id}`, { method: "DELETE" }),
  surfaces: () =>
    api<{ surfaces: Array<Record<string, unknown>> }>(
      "/v1/platform/ai/surfaces",
    ),
  putSurfaces: (body: Record<string, unknown>) =>
    api("/v1/platform/ai/surfaces", { method: "PUT", body }),
};
